import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api-client';

/** A passkey as listed in Settings. Mirrors the API's `WebauthnCredentialSummary`. */
export interface PasskeySummary {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: string | null;
  backedUp: boolean;
}

interface RegistrationChallenge {
  challengeId: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

interface AuthenticationChallenge {
  challengeId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export const passkeyKeys = {
  all: ['passkeys'] as const,
};

/**
 * Whether this browser can actually complete a passkey ceremony *on this
 * device*. Both checks matter and they are not the same question:
 * `browserSupportsWebAuthn` only says the API exists, while
 * `platformAuthenticatorIsAvailable` says a built-in authenticator (Touch ID,
 * Windows Hello, Android biometrics) is present and enrolled.
 *
 * Callers must treat `null` as "don't render anything yet". The last attempt at
 * this feature shipped a button whose only behaviour was to explain it did not
 * work; offering the option before we know it is real is the failure mode this
 * function exists to prevent.
 */
export async function detectPasskeySupport(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!window.navigator?.credentials || !browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    // A browser that throws while being asked is one we should not promise
    // anything to.
    return false;
  }
}

/** True when the user dismissed or cancelled the OS prompt — not an error worth shouting about. */
export function isPasskeyCancellation(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

// ── Sign-in ─────────────────────────────────────────────────────────────────

/**
 * Run the full sign-in ceremony. On success gov-api has set the same httpOnly
 * session cookies as a password login, and the resolved user is returned.
 */
export async function signInWithPasskey(): Promise<unknown> {
  const { data: challenge } = await api.post<AuthenticationChallenge>(
    '/auth/webauthn/authentication/challenge',
  );
  const assertion = await startAuthentication({ optionsJSON: challenge.options });
  const { data } = await api.post<{ user: unknown }>('/auth/webauthn/authentication/verify', {
    challengeId: challenge.challengeId,
    response: assertion,
  });
  return data.user;
}

// ── Enrolment / management ──────────────────────────────────────────────────

export async function fetchPasskeys(): Promise<PasskeySummary[]> {
  const { data } = await api.get<PasskeySummary[]>('/auth/webauthn/credentials');
  return data;
}

export async function enrollPasskey(label: string): Promise<PasskeySummary> {
  const { data: challenge } = await api.post<RegistrationChallenge>(
    '/auth/webauthn/registration/challenge',
  );
  const attestation = await startRegistration({ optionsJSON: challenge.options });
  const { data } = await api.post<PasskeySummary>('/auth/webauthn/registration/verify', {
    challengeId: challenge.challengeId,
    label,
    response: attestation,
  });
  return data;
}

export async function revokePasskey(id: string): Promise<void> {
  await api.delete(`/auth/webauthn/credentials/${id}`);
}

export function usePasskeys(enabled = true) {
  return useQuery({ queryKey: passkeyKeys.all, queryFn: fetchPasskeys, enabled });
}

export function useEnrollPasskey() {
  const qc = useQueryClient();
  return useMutation({
    // The settings section shows enrolment failures inline.
    meta: { silent: true },
    mutationFn: enrollPasskey,
    onSuccess: () => qc.invalidateQueries({ queryKey: passkeyKeys.all }),
  });
}

export function useRevokePasskey() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: revokePasskey,
    onSuccess: () => qc.invalidateQueries({ queryKey: passkeyKeys.all }),
  });
}
